import React, { useState, useMemo } from 'react';
import {
  useGetMessagesQuery, useEnvoyerMessageMutation, useDeleteMessageMutation,
  useMarquerTousLusMutation, MessageInterne,
} from '../../services/messagerieApi';
import { Send, Inbox, Mail, Trash2, CheckCheck, Users, Search, X, Info, AlertTriangle, AlertOctagon, Award, Clock, GraduationCap } from 'lucide-react';
import { Spinner } from '../../components/ui/Spinner';
import { useGetSurveillanceQuery } from '../../services/coreApi';
import MessagesEtudiantsView from './MessagesEtudiantsView';
import { useGetMessagesEtudiantsQuery } from '../../services/messagesEtudiantsApi';

const TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  info: { label: 'Information', icon: <Info size={14} />, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
  important: { label: 'Important', icon: <AlertTriangle size={14} />, color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
  urgent: { label: 'Urgent', icon: <AlertOctagon size={14} />, color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
  felicitation: { label: 'Felicitation', icon: <Award size={14} />, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
  rappel: { label: 'Rappel', icon: <Clock size={14} />, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
};

type Section = 'internes' | 'etudiants';

const MessagerieTab: React.FC = () => {
  const [section, setSection] = useState<Section>('internes');
  const [view, setView] = useState<'envoyer' | 'recus' | 'envoyes'>('envoyer');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Compteur global des messages étudiants non traités (pour badge sur le toggle)
  const { data: msgEtudiantsStats } = useGetMessagesEtudiantsQuery({ statut: 'non_traites', limit: 1 });
  const nbEtudiantsNonTraites = msgEtudiantsStats?.nb_non_traites || 0;

  // Formulaire
  const [destinataireIds, setDestinataireIds] = useState<number[]>([]);
  const [broadcast, setBroadcast] = useState(false);
  const [typeMessage, setTypeMessage] = useState('info');
  const [sujet, setSujet] = useState('');
  const [contenu, setContenu] = useState('');
  const [searchUser, setSearchUser] = useState('');

  // Queries
  const { data: messagesRecus = [], isLoading: loadingRecus, refetch: refetchRecus } = useGetMessagesQuery({ boite: 'recus' });
  const { data: messagesEnvoyes = [], isLoading: loadingEnvoyes } = useGetMessagesQuery({ boite: 'envoyes' });
  const { data: surveillance } = useGetSurveillanceQuery();
  const [envoyerMsg, { isLoading: envoyant }] = useEnvoyerMessageMutation();
  const [deleteMsg] = useDeleteMessageMutation();
  const [marquerTousLus] = useMarquerTousLusMutation();

  // Liste des utilisateurs (depuis surveillance)
  const utilisateurs = useMemo(() => {
    if (!surveillance) return [];
    return [...(surveillance.en_ligne || []), ...(surveillance.hors_ligne || [])].sort((a: any, b: any) => a.nom.localeCompare(b.nom));
  }, [surveillance]);

  const utilisateursFiltres = useMemo(() => {
    if (!searchUser) return utilisateurs;
    const q = searchUser.toLowerCase();
    return utilisateurs.filter((u: any) => u.nom.toLowerCase().includes(q) || u.username.toLowerCase().includes(q));
  }, [utilisateurs, searchUser]);

  const handleEnvoyer = async () => {
    if (!sujet.trim() || !contenu.trim()) {
      setMessage({ type: 'error', text: 'Sujet et contenu requis' });
      return;
    }
    if (!broadcast && destinataireIds.length === 0) {
      setMessage({ type: 'error', text: 'Selectionnez au moins un destinataire ou cochez "Tous"' });
      return;
    }
    try {
      const res = await envoyerMsg({
        destinataire_ids: broadcast ? undefined : destinataireIds,
        broadcast,
        sujet: sujet.trim(),
        contenu: contenu.trim(),
        type_message: typeMessage,
      }).unwrap();
      setMessage({ type: 'success', text: res.message });
      setSujet('');
      setContenu('');
      setDestinataireIds([]);
      setBroadcast(false);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.error || 'Erreur' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    await deleteMsg(id).unwrap();
    refetchRecus();
  };

  const toggleDestinataire = (id: number) => {
    setDestinataireIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="space-y-4">
      {/* Bascule Internes / Étudiants */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-0.5 rounded-lg w-fit">
        <button
          onClick={() => setSection('internes')}
          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
            section === 'internes'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <Users size={13} /> Internes
        </button>
        <button
          onClick={() => setSection('etudiants')}
          className={`px-3 py-1.5 rounded text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
            section === 'etudiants'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <GraduationCap size={13} /> Étudiants
          {nbEtudiantsNonTraites > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold bg-orange-600 text-white rounded-full px-1">
              {nbEtudiantsNonTraites}
            </span>
          )}
        </button>
      </div>

      {section === 'etudiants' ? (
        <MessagesEtudiantsView />
      ) : (
        <>
          {/* Navigation interne */}
          <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 pb-3">
            {[
              { key: 'envoyer' as const, label: 'Nouveau message', icon: <Send size={14} /> },
              { key: 'recus' as const, label: `Recus (${messagesRecus.filter(m => !m.lu).length})`, icon: <Inbox size={14} /> },
              { key: 'envoyes' as const, label: `Envoyes (${messagesEnvoyes.length})`, icon: <Mail size={14} /> },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setView(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg font-medium transition-colors ${
                  view === tab.key
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Message feedback */}
          {message && (
            <div className={`p-3 rounded-lg text-sm flex items-center justify-between ${
              message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            }`}>
              {message.text}
              <button onClick={() => setMessage(null)}><X size={14} /></button>
            </div>
          )}

          {/* === VUE ENVOYER === */}
          {view === 'envoyer' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Type de message</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => setTypeMessage(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${
                        typeMessage === key
                          ? `${cfg.bg} border-current`
                          : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      {cfg.icon} {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Destinataires</label>
                <label className="flex items-center gap-2 mb-2 cursor-pointer">
                  <input type="checkbox" checked={broadcast} onChange={(e) => { setBroadcast(e.target.checked); if (e.target.checked) setDestinataireIds([]); }}
                    className="rounded w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Envoyer a <strong>tous les utilisateurs</strong></span>
                  <Users size={14} className="text-gray-400" />
                </label>

                {!broadcast && (
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 flex items-center gap-2">
                      <Search size={14} className="text-gray-400" />
                      <input type="text" value={searchUser} onChange={(e) => setSearchUser(e.target.value)}
                        placeholder="Rechercher un utilisateur..." className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none" />
                      {destinataireIds.length > 0 && (
                        <span className="text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                          {destinataireIds.length} selectionne(s)
                        </span>
                      )}
                    </div>
                    <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                      {utilisateursFiltres.map((u: any) => (
                        <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/10 cursor-pointer">
                          <input type="checkbox" checked={destinataireIds.includes(u.id)} onChange={() => toggleDestinataire(u.id)}
                            className="rounded w-3.5 h-3.5 text-blue-600" />
                          <span className="text-xs text-gray-800 dark:text-gray-200 flex-1">{u.nom || u.username}</span>
                          <span className="text-[10px] text-gray-400">{u.role}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sujet</label>
                <input type="text" value={sujet} onChange={(e) => setSujet(e.target.value)} placeholder="Objet du message..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Message</label>
                <textarea value={contenu} onChange={(e) => setContenu(e.target.value)} rows={5} placeholder="Redigez votre message..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>

              <div className="flex justify-end">
                <button onClick={handleEnvoyer} disabled={envoyant}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50">
                  {envoyant ? <Spinner size="sm" /> : <Send size={16} />}
                  Envoyer
                </button>
              </div>
            </div>
          )}

          {/* === VUE MESSAGES RECUS === */}
          {view === 'recus' && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-gray-500">{messagesRecus.length} message(s)</span>
                {messagesRecus.filter(m => !m.lu).length > 0 && (
                  <button onClick={() => marquerTousLus().unwrap().then(() => refetchRecus())}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700">
                    <CheckCheck size={14} /> Tout marquer comme lu
                  </button>
                )}
              </div>
              {loadingRecus ? <Spinner /> : messagesRecus.length === 0 ? (
                <p className="text-center py-8 text-gray-400">Aucun message recu</p>
              ) : (
                <div className="space-y-2">
                  {messagesRecus.map(m => {
                    const cfg = TYPE_CONFIG[m.type_message] || TYPE_CONFIG.info;
                    return (
                      <div key={m.id} className={`border rounded-lg p-3 ${!m.lu ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/5' : 'border-gray-200 dark:border-gray-700'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.bg}`}>
                                {cfg.icon} {cfg.label}
                              </span>
                              {!m.lu && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                              <span className="text-xs text-gray-400">{new Date(m.date_envoi).toLocaleString('fr-FR')}</span>
                            </div>
                            <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mt-1">{m.sujet}</div>
                            <div className="text-xs text-gray-500 mt-0.5">De : {m.expediteur_nom}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">{m.contenu}</div>
                          </div>
                          <button onClick={() => handleDelete(m.id)} className="text-gray-400 hover:text-red-500 shrink-0">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* === VUE MESSAGES ENVOYES === */}
          {view === 'envoyes' && (
            <div>
              <span className="text-sm text-gray-500 mb-3 block">{messagesEnvoyes.length} message(s) envoye(s)</span>
              {loadingEnvoyes ? <Spinner /> : messagesEnvoyes.length === 0 ? (
                <p className="text-center py-8 text-gray-400">Aucun message envoye</p>
              ) : (
                <div className="space-y-2">
                  {messagesEnvoyes.map(m => {
                    const cfg = TYPE_CONFIG[m.type_message] || TYPE_CONFIG.info;
                    return (
                      <div key={m.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cfg.bg}`}>
                            {cfg.icon} {cfg.label}
                          </span>
                          <span className="text-xs text-gray-400">{new Date(m.date_envoi).toLocaleString('fr-FR')}</span>
                          {m.lu && <span className="text-[10px] text-green-600">Lu</span>}
                          {m.est_broadcast && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">Broadcast</span>}
                        </div>
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mt-1">{m.sujet}</div>
                        <div className="text-xs text-gray-500 mt-0.5">A : {m.destinataire_nom}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{m.contenu}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MessagerieTab;
